import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  uploadFacultyPhotoViaAdmin,
  startFacultyIdCardJob,
  getFacultyIdCardJobStatus,
  downloadFacultyIdCardZip,
} from "../../../utils/Api";
import { toast } from "react-toastify";
const formatDob = (d) => { if (!d) return ""; const x = new Date(d); return isNaN(x) ? String(d) : x.toLocaleDateString("en-GB", { timeZone: "UTC" }).replace(/\//g, "-"); };

const FacultyIdCardTemplate = (faculty) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Faculty ID Card</title>
  <style>
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; margin: 0; padding: 12px; background: #f3f4f6; zoom: 1.5; }
    .wrap { display: flex; gap: 12px; justify-content: center; }
    .id-card { width: 54mm; height: 85.6mm; background: #fff; display: flex; flex-direction: column; overflow: hidden; border: 1px solid #d1d5db; border-radius: 3mm; }
    .header { display: flex; align-items: center; gap: 1.5mm; padding: 2.5mm 2mm 0; }
    .header img { width: 11mm; height: 11mm; object-fit: contain; flex-shrink: 0; }
    .header-text { flex: 1; text-align: center; line-height: 1.25; }
    .charities { font-size: 5pt; font-weight: 700; }
    .college { font-size: 7.5pt; font-weight: 700; }
    .address { font-size: 3.6pt; color: #374151; }
    .photo-wrap { display: flex; justify-content: center; margin-top: 3mm; }
    .photo { width: 27mm; height: 33mm; object-fit: cover; border: 0.3mm solid #111; }
    .details { padding: 3mm 4mm 0; font-size: 7pt; line-height: 1.6; }
    .label { font-weight: 700; color: #9f1239; }
    .value { font-weight: 700; color: #111; }
    .sign-row { margin-top: auto; display: flex; justify-content: space-around; padding: 0 2mm 1mm; }
    .sign { width: 20mm; height: 8mm; }
    .sign img { width: 100%; height: 100%; object-fit: contain; }
    .footer { display: flex; justify-content: space-around; background: #fbbf24; padding: 1.2mm 0; font-size: 4.5pt; font-weight: 700; }
    .back { flex: 1; display: flex; flex-direction: column; padding: 8mm 4mm 6mm; font-size: 6.5pt; line-height: 1.7; }
    .back .label { color: #111; }
    .back .value { font-weight: 400; }
    .return { margin-top: auto; font-size: 6pt; line-height: 1.5; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="id-card">
      <div class="header">
        <img src="/agnel-logo.png" alt="Logo">
        <div class="header-text">
          <div class="charities">AGNEL CHARITIES</div>
          <div class="college">AGNEL SCHOOL OF LAW</div>
          <div class="address">Agnel Technical Education Complex, Sector-9A, Vashi, Navi Mumbai.<br>www.agnelschooloflaw.com | +91 2227771000 | asl@agnelschooloflaw.com</div>
        </div>
      </div>
      <div class="photo-wrap"><img class="photo" src="${faculty.photo || "/fallback-image.png"}" alt="Photo"></div>
      <div class="details">
        <div><span class="label">NAME :</span> <span class="value">${(faculty.facultyName || "").toUpperCase()}</span></div>
        <div><span class="label">Designation :</span> <span class="value">${faculty.designation || "Faculty"}</span></div>
        <div><span class="label">Date of Birth :</span> <span class="value">${formatDob(faculty.dateOfBirth || faculty.dob)}</span></div>
      </div>
      <div class="sign-row">
        <div class="sign"><img src="/principal_sign.png" alt=""></div>
        <div class="sign"><img src="/clg_seal.png" alt=""></div>
      </div>
      <div class="footer"><span>Principal</span><span>College Seal</span></div>
    </div>
    <div class="id-card">
      <div class="back">
        <div><span class="label">Mobile No :</span> <span class="value">${faculty.phone || ""}</span></div>
        <div><span class="label">Blood Group :</span> <span class="value">${faculty.bloodGroup || ""}</span></div>
        <div><span class="label">E-mail :</span> <span class="value">${faculty.email || ""}</span></div>
        <div><span class="label">Address :</span> <span class="value">${faculty.address || ""}</span></div>
        <div class="return"><b>If Found please return to:</b><br>Agnel School of Law,<br>Sector-9A, Vashi, Navi Mumbai,<br>Maharashtra, India, PIN - 400703.</div>
      </div>
    </div>
  </div>
</body>
</html>
`;

const FacultyIdCardModal = ({ open, onClose, faculty, onPhotoUploaded }) => {
  const [cardHtml, setCardHtml] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (faculty) {
      setCardHtml(FacultyIdCardTemplate(faculty));
    }
  }, [faculty]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("photo", file);
      const response = await uploadFacultyPhotoViaAdmin(faculty._id, formData);
      toast.success("Photo uploaded successfully");
      onPhotoUploaded(response.faculty);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const { jobId } = await startFacultyIdCardJob([faculty._id]);
      const poll = setInterval(async () => {
        const status = await getFacultyIdCardJobStatus(jobId);
        if (status.status === "completed") {
          clearInterval(poll);
          await downloadFacultyIdCardZip(jobId);
          setDownloading(false);
        } else if (status.status === "failed") {
          clearInterval(poll);
          toast.error("ID card generation failed");
          setDownloading(false);
        }
      }, 2000);
    } catch (error) {
      toast.error("Failed to generate ID card");
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className="relative p-4">
        <div className="absolute top-0 right-0 m-2 flex gap-2 z-10">
          <label className="cursor-pointer px-2 py-1 rounded-full bg-blue-100 hover:bg-blue-200">
            <CloudUploadIcon />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={uploading}
            />
          </label>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            {downloading ? "..." : <DownloadIcon />}
          </button>
        </div>
        {uploading && (
          <p className="text-center text-sm text-blue-500 mb-2">Uploading photo...</p>
        )}
        {downloading && (
          <p className="text-center text-sm text-blue-500 mb-2">Generating PDF, please wait...</p>
        )}
        <iframe
          srcDoc={cardHtml}
          title="Faculty ID Card Preview"
          style={{ width: "100%", height: "560px", border: "none" }}
        />
      </div>
    </Dialog>
  );
};

export default FacultyIdCardModal;